import type { BookingMode, LocationDetail, LocationSearchResult } from '@/api/accommodation/types'

/**
 * Minimal location shape accepted by the mappers — works for both full
 * LocationDetail and the lighter location attached to search results / orders.
 */
export type LocationLike = Pick<LocationDetail, 'id' | 'name' | 'address' | 'region' | 'category'> & {
  bookingMode?: BookingMode
}

/** The unit sold — capacity lives on the unit, not on the location. */
export interface UnitLike {
  id: number
  name: string
  totals: { beds: number; bedrooms: number }
}

/** Price bundle shared by GTM and Mixpanel payloads. */
export interface Prices {
  dailyDiscountPriceFrom?: number
  dailyStandardPriceFrom?: number
  totalStandardPrice?: number
  totalDiscountPrice?: number
}

export interface SearchFilters {
  fromDate?: Date
  toDate?: Date
  region?: string
  labelId?: number
  beds?: number
  cribs?: number
  adult?: boolean
  pets?: boolean
  /** Comma-separated facility keys (e.g. "sauna,pool"). */
  facility?: string
  /** Comma-separated category slugs (e.g. "chata,glamping"). */
  category?: string
  order?: string
  direction?: string
}

// =============================================================================
// Accommodation shape (nested, camelCase) — single canonical mapping shared by
// GTM and Mixpanel. GTM is the source of truth; there is no separate Mixpanel
// shape.
// =============================================================================

export interface Accommodation {
  /** Location (place) ID — kept stable across the unit model for GTM/GA4. */
  id: number
  unitId?: number
  /** Unit name — the location name for a single unit carrying it. */
  unitName?: string
  city: string
  name: string
  regionId: number
  regionName: string
  beds?: number
  rooms?: number
  categoryId: number
  categoryName: string
  /** instant / request — compares the funnels of the two booking modes. */
  bookingMode?: BookingMode
}

export function mapAccommodation(source: LocationLike, unit?: UnitLike): Accommodation {
  return {
    id: source.id,
    unitId: unit?.id,
    unitName: unit?.name,
    name: source.name,
    city: source.address.city,
    regionId: source.region.id,
    regionName: source.region.name,
    beds: unit?.totals.beds,
    rooms: unit?.totals.bedrooms,
    categoryId: source.category.id,
    categoryName: source.category.name,
    bookingMode: source.bookingMode,
  }
}

export interface Offer {
  accommodation: Accommodation
  prices: Prices
  dateFrom?: Date
  dateTo?: Date
}

export function mapOffer(source: LocationSearchResult): Offer {
  return {
    accommodation: mapAccommodation(source.location, source.unit),
    prices: {
      dailyDiscountPriceFrom: source.minDiscountPrice,
      dailyStandardPriceFrom: source.standardPrice,
      totalDiscountPrice: undefined,
      totalStandardPrice: undefined,
    },
    dateFrom: source.start,
    dateTo: source.end,
  }
}

/** Whole nights between two dates. */
export function nightsBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
}

/** Currency for all monetary tracking events — the site is CZK-only. */
export const CURRENCY = 'CZK'

/**
 * Meta-compatible `value` for an event, in CZK. Prefers the most specific price
 * available: the total for a selected stay, otherwise the daily "from" price.
 * Returns undefined when no price is known, so the field is omitted rather than
 * sent as 0 (which Meta would treat as a real zero-value conversion).
 */
export function trackingValue(prices: Prices | undefined): number | undefined {
  return (
    prices?.totalDiscountPrice ??
    prices?.totalStandardPrice ??
    prices?.dailyDiscountPriceFrom ??
    prices?.dailyStandardPriceFrom
  )
}

/**
 * Meta catalog fields for the unit's current offers. `content_ids` are the
 * offer UUIDs, which match the Meta feed's `g:id`, enabling dynamic-ads
 * retargeting. Returns an empty object when no offers are present, so both
 * fields are omitted together (content_type without content_ids is useless).
 */
export function offerContentData(accommodation: {
  unit: { offers: { id: string }[] }
}): { content_ids: string[]; content_type: 'product' } | Record<string, never> {
  const ids = accommodation.unit.offers.map((offer) => offer.id)
  return ids.length > 0 ? { content_ids: ids, content_type: 'product' } : {}
}
